/**
 * Image utility functions for processing profile pictures
 */

import { consts } from "../consts";

/**
 * Resize and crop an image to a square of the specified size
 * @param imageFile - The image file or data URL
 * @param size - The target size (width and height)
 * @returns Promise that resolves to a base64 data URL of the processed image
 */
export async function resizeAndCropImage(
  imageFile: File | string,
  size: number = Math.min(consts.avatar.width, consts.avatar.height)
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    const handleImageLoad = () => {
      try {
        // Create canvas for processing
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Calculate dimensions to maintain aspect ratio and fill square
        const minDimension = Math.min(img.width, img.height);
        const scale = size / minDimension;
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image in the square
        const x = (size - scaledWidth) / 2;
        const y = (size - scaledHeight) / 2;
        
        // Draw the image
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        
        // Convert to data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

        // Display length in kb
        console.info(`Avatar Resize&Crop data URL Length: ${(dataUrl.length / 1024).toFixed(2)}kb`); 

        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    if (typeof imageFile === 'string') {
      img.src = imageFile;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(imageFile);
    }
    
    img.onload = handleImageLoad;
  });
}

/**
 * Crop a specific region from an image and resize to target size
 * @param imageDataUrl - The source image as a data URL
 * @param cropArea - The crop area {x, y, width, height} in pixels
 * @param targetSize - The target size for the output
 * @returns Promise that resolves to a base64 data URL of the cropped image
 */
export async function cropImage(
  imageDataUrl: string,
  cropArea: { x: number; y: number; width: number; height: number },
  targetWidth: number = consts.avatar.width,
  targetHeight: number = consts.avatar.height
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw the cropped region
        ctx.drawImage(
          img,
          cropArea.x, cropArea.y, cropArea.width, cropArea.height,
          0, 0, targetWidth, targetHeight
        );
        
        // console.log();
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageDataUrl;
  });
}
